import { LitElement, html, css, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { applyRole, applyResponsiveProp, parseProp, resolveGap, resolvePad, resolveCols } from '../utils.js';

/**
 * uwc-grid — CSS Grid layout
 *
 * Two-dimensional layout via `grid-template-columns`. The `cols` attribute
 * accepts either a number (creates equal-width `1fr` columns) or any valid
 * `grid-template-columns` value, with the responsive breakpoint prefix syntax.
 *
 * Philosophy: reach for `uwc-grid` when items sit in rows and columns, or
 * when you need a two-dimensional layout like a card grid, a form, or a
 * sidebar+content split. For single-axis layouts prefer `uwc-stack`.
 *
 * Breakpoints: sm ≥ 640px · md ≥ 768px · lg ≥ 1024px · xl ≥ 1280px
 *
 * @attr {string} cols    - Column template. "3" → repeat(3,1fr). Responsive: "1 sm:2 lg:4"
 * @attr {string} rows    - Row template. CSS value, e.g. "auto 1fr auto"
 * @attr {string} gap     - Gap token (none xs sm md lg xl 2xl) or any CSS value
 * @attr {string} align   - align-items
 * @attr {string} justify - justify-items
 * @attr {string} as      - Semantic landmark role
 * @attr {string} pad     - Padding token or CSS value
 *
 * @cssprop --uwc-grid-gap - Override gap
 * @cssprop --uwc-grid-pad - Override padding
 */
export class UwcGrid extends LitElement {
  static override styles = css`
    :host {
      display:               grid;
      width:                 100%;
      min-width:             0;
      grid-template-columns: var(--_cols, 1fr);
      grid-template-rows:    var(--_rows, auto);
      gap:                   var(--uwc-grid-gap, var(--_gap, 0));
      align-items:           var(--_align,   stretch);
      justify-items:         var(--_justify, stretch);
      padding:               var(--uwc-grid-pad, var(--_pad, 0));
      box-sizing:            border-box;
    }

    /* ── Responsive columns ──────────────────────────────────────────────────── */
    @media (min-width: 640px) {
      :host { grid-template-columns: var(--_cols-sm,  var(--_cols, 1fr)); }
    }
    @media (min-width: 768px) {
      :host { grid-template-columns: var(--_cols-md,  var(--_cols-sm,  var(--_cols, 1fr))); }
    }
    @media (min-width: 1024px) {
      :host { grid-template-columns: var(--_cols-lg,  var(--_cols-md,  var(--_cols-sm,  var(--_cols, 1fr)))); }
    }
    @media (min-width: 1280px) {
      :host { grid-template-columns: var(--_cols-xl,  var(--_cols-lg,  var(--_cols-md,  var(--_cols-sm,  var(--_cols, 1fr))))); }
    }
  `;

  /** Column template. Number or CSS value. Supports responsive syntax. */
  @property({ type: String })                cols    = '1';
  /** Row template. CSS value. */
  @property({ type: String })                rows    = '';
  /** Gap between cells. */
  @property({ type: String })                gap     = '';
  /** align-items value. */
  @property({ type: String })                align   = '';
  /** justify-items value. */
  @property({ type: String })                justify = '';
  /** Maps to an ARIA landmark role. */
  @property({ type: String, reflect: true }) as      = '';
  /** Padding token or CSS value. */
  @property({ type: String })                pad     = '';

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('as'))      applyRole(this, this.as);
    if (changed.has('gap'))     this.style.setProperty('--_gap',     resolveGap(this.gap));
    if (changed.has('pad'))     this.style.setProperty('--_pad',     resolvePad(this.pad));
    if (changed.has('align'))   this.style.setProperty('--_align',   this.align   || 'stretch');
    if (changed.has('justify')) this.style.setProperty('--_justify', this.justify || 'stretch');
    if (changed.has('rows'))    this.style.setProperty('--_rows',    this.rows    || 'auto');
    if (changed.has('cols'))    applyResponsiveProp(this, parseProp(this.cols || '1'), '--_cols', resolveCols);
  }

  override render() { return html`<slot></slot>`; }
}
